import { html, TemplateResult, CSSResultGroup } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { OutlineElement } from '../../base/outline-element/outline-element';
import { SlotsNGController as SlotController } from '../../controllers/slots-ng-controller';
import { ResizeController } from '../../controllers/resize-controller';
import { screenSizeMap } from '../../controllers/screen-size-controller';
import { backgroundColor } from '../sic-container/sic-container';
import componentStyles from './sic-quote-block.css.lit';
import quoteVars from './css-variables/sic-quote-block-vars.css.lit';
export type imageOrientation = 'left' | 'right' | null;

/**
 * The Quote Block component
 *
 * @element sic-quote-block
 * @slot title
 * @slot quote
 * @slot author
 * @slot attribution
 * @slot cta (optional)
 * @slot quote-image (optional)
 *
 * @prop {String} background-color
 * @prop {String} image-orientation (optional): left|right
 * @prop {String} icon-name
 *
 * Lightdom styles in sic-quote-block.lightdom.css included in /shared.css
 */

@customElement('sic-quote-block')
export class SicQuoteBlock extends OutlineElement {
  slots = new SlotController(this);
  resizeController: ResizeController;
  static styles: CSSResultGroup = [quoteVars, componentStyles];

  // The resizeController is initialized here because visibleBreakpoint is not set yet when constructor runs
  connectedCallback() {
    super.connectedCallback();
    this.resizeController = new ResizeController(this, {
      breakpoints: [screenSizeMap.get(this.visibleBreakpoint) as number],
      containerQuery: false,
    });
  }

  @property({ type: String, attribute: 'image-orientation' })
  imageOrientation: imageOrientation = 'left';

  @property({ type: String, attribute: 'background-color' })
  backgroundColor: backgroundColor = 'primary-default';

  @property({ type: String, attribute: 'quote' })
  iconName = 'quote';

  /**
   * The breakpoint at which the switch from mobile to desktop layout.
   *
   * This is done as a variable to ensure flexibility for future usages.
   */
  @property({ type: String, attribute: 'breakpoint' })
  visibleBreakpoint = 'lg';

  @state() private isMobile = false;
  @state() private hasMedia = false;

  // Quote color is sent by css var set in container background color.
  quoteIconTemplate(): TemplateResult {
    return html`
      <div class="quotation">
        <sic-icon
          name="${this.iconName}"
          size="100%"
          class="icon-style"
        ></sic-icon>
      </div>
    `;
  }
  headingTemplate(): TemplateResult {
    if (this.slots.slotExists('title')) {
      return html` <sic-heading level-size="4">
        ${this.slots.conditionalSlot('title')}
      </sic-heading>`;
    }
    return html``;
  }
  authorTemplate(): TemplateResult {
    if (this.slots.slotExists('author')) {
      return html` <sic-heading level-size="2">
        ${this.slots.conditionalSlot('author')}
      </sic-heading>`;
    }
    return html``;
  }

  contentTemplate(): TemplateResult {
    return html`
      <figure class="content">
        ${this.quoteIconTemplate()} ${this.headingTemplate()}
        <blockquote>
          ${this.slots.conditionalSlot('quote')}
          <hr />
        </blockquote>
        <figcaption>
          ${this.authorTemplate()} ${this.slots.conditionalSlot('attribution')}
        </figcaption>
        ${this.slots.conditionalSlot('cta', false)}
      </figure>
    `;
  }
  //image vs no image
  //image left vs image right (at small screens, just becomes image top)
  layoutTemplate(): TemplateResult {
    this.isMobile = this.resizeController.currentBreakpointRange === 0;
    this.hasMedia = this.slots.slotHasContent('quote-image');
    const layoutClasses = {
      'quote-layout': true,
      'layout-is-mobile': this.isMobile,
      'layout-has-media': this.hasMedia,
      'layout-image-right': this.hasMedia && this.imageOrientation == 'right',
    };
    // TODO check if image-wrapper is needed.  If not can simplify below.
    if (this.isMobile || (this.hasMedia && this.imageOrientation == 'left')) {
      return html`
        <div class="${classMap(layoutClasses)}">
          ${this.slots.conditionalSlot('quote-image', false, 'image-wrapper')}
          ${this.contentTemplate()}
        </div>
      `;
    } else if (this.hasMedia && this.imageOrientation == 'right') {
      return html`
        <div class="${classMap(layoutClasses)}">
          ${this.contentTemplate()}
          ${this.slots.conditionalSlot('quote-image', false, 'image-wrapper')}
        </div>
      `;
    } else {
      return html` <div class="${classMap(layoutClasses)}">
        ${this.contentTemplate()}
      </div>`;
    }
  }

  render(): TemplateResult {
    return html`<div class="quote-block--wrapper">
      <sic-container
        background-color=${this.backgroundColor}
        container-width="full"
        top-padding="none"
        bottom-padding="none"
        class="quote-block--inner"
      >
        ${this.layoutTemplate()}
      </sic-container>
    </div>`;
  }
}
